import { useCallback, useMemo, useState } from 'react'
import type { BandSort } from 'app/components/action-center/treatmentSort'
import { useLocationsInfo } from 'app/components/action-center/useLocationNames'
import { useTreatmentCaseBoard } from 'app/components/action-center/useTreatmentCaseBoard'
import { TREATMENT_PLANS_TAB } from 'app/components/contacts/ContactRelatedObjects'
import type { FilterObject } from 'app/filter.utils'
import { useCdtCodeMappings } from 'app/hooks/useCdtCodeMappings'
import { useDrawerContact } from 'app/hooks/useDrawerContact'
import { useSavedListTab } from 'app/hooks/useSavedListTab'
import { useHandleSearchParams } from 'app/utils'
import { exportTreatmentRows } from 'lib/treatmentExport'

const DEFAULT_SORT: BandSort = { sort: `presented`, direction: `DESC` }
const EXPORT_FILENAME = `treatment-plans.csv`
// Excel reads a CSV without a byte order mark as the system code page and garbles accented
// names; react-csv, behind every other export in the app, writes one too.
const UTF8_BOM = `﻿`

export function useTreatmentPlansPage() {
  const handleSearchParams = useHandleSearchParams()
  const [sort, setSort] = useState<BandSort>(DEFAULT_SORT)
  const locationNames = useLocationsInfo()
  const { categoryOf } = useCdtCodeMappings()
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>([])

  // handleSearchParams reads the live URL when it runs, so the first render's copy serves
  // every click; depending on it would hand the table new columns on every render.
  const openRecord = useCallback((contactId: string) => {
    handleSearchParams({ drawer: contactId })
    return true
  }, [])
  const saved = useSavedListTab(`treatmentPlans`)
  const listPage = saved.list?.page
  const rule = useMemo(
    () =>
      listPage
        ? {
            filters: (listPage.filters ?? []) as unknown as FilterObject[],
            join: listPage.join === `or` ? (`or` as const) : (`and` as const),
          }
        : undefined,
    [listPage],
  )

  const cases = useTreatmentCaseBoard({ sort: sort.sort, direction: sort.direction, rule, openRecord })
  const { board } = cases

  const drawerContact = useDrawerContact()

  const exportRows = () => {
    const csv = exportTreatmentRows(board.rows, visibleColumnIds, categoryOf, locationNames)
    const url = URL.createObjectURL(new Blob([UTF8_BOM, csv], { type: `text/csv;charset=utf-8` }))
    const link = document.createElement(`a`)
    link.href = url
    link.download = EXPORT_FILENAME
    link.click()
    // Revoked a tick later: some browsers start the download after click() returns.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  return {
    board,
    // Until the list's rule is in hand the board is reading every plan, which it must not show.
    ready: cases.ready && saved.settled,
    list: saved.list && { name: saved.list.name, edit: saved.list.edit },
    sort,
    setSort,
    openRecord: cases.openRowRecord,
    setVisibleColumnIds,
    exportRows,
    drawer: {
      ...drawerContact,
      // Opened from a treatment row, the record lands on its treatment, not the timeline.
      initialTab: TREATMENT_PLANS_TAB,
    },
  }
}
